"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
var _reactRedux = require("react-redux");
var _toggleProgramSource = _interopRequireDefault(require("../program/toggleProgramSource"));
var _setSoloLive = _interopRequireDefault(require("../program/setSoloLive"));
var _logger = require("@studio/logger");
var _sources = require("@studio/sources");
var _utils = require("../../utils");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }
var LOG = new _logger.StudioLogger('takeGenericPresenterLive');

/**
 * Redux thunk action to add or remove a generic presenter source (camera or screen) to/from the program.
 * Handles camera enablement, mute status, solo live mode, and camera limit enforcement.
 *
 * @param {Object} source - The source object to add/remove from program
 * @param {string} source._id - Unique identifier for the source
 * @param {string} source.value - Attendee ID associated with the source
 * @param {string} source.type - Type of source (e.g., CAMERA_PRESENTER, SCREEN_PRESENTER)
 * @param {boolean} [source.cameraUnavailable] - Whether the camera is unavailable
 * @param {Object} [sourceActionConfig={}] - Configuration options for the action
 * @param {boolean} [sourceActionConfig.isSoloLive] - If true, sets this source as solo live (removing others)
 * @param {Function} setProgram - Function to update the program state
 * @param {Function} toggleAttendeeCameraStatus - Function to toggle attendee camera on/off
 * @param {Function} toggleAttendeeMuteStatus - Function to toggle attendee mute status
 * @returns {Function} Redux thunk function that dispatches appropriate actions based on configuration
 *
 */
function takeGenericPresenterLive(source) {
  var sourceActionConfig = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var setProgram = arguments.length > 2 ? arguments[2] : undefined;
  var toggleAttendeeCameraStatus = arguments.length > 3 ? arguments[3] : undefined;
  var toggleAttendeeMuteStatus = arguments.length > 4 ? arguments[4] : undefined;
  return function (dispatch, getState) {
    var _getState = getState(),
      attendeeCameraStatus = _getState.attendeeCameraStatus,
      attendeeMuteStatus = _getState.attendeeMuteStatus,
      _getState$program$lay = _getState.program.layers,
      layers = _getState$program$lay === void 0 ? [] : _getState$program$lay,
      _getState$broadcastSe = _getState.broadcastSettings.autoMutePresenters,
      autoMutePresenters = _getState$broadcastSe === void 0 ? true : _getState$broadcastSe,
      broadcastSources = _getState.broadcastSources,
      _getState$meeting = _getState.meeting,
      meetingId = _getState$meeting.meetingId,
      attendeeTiles = _getState$meeting.attendeeTiles;
    var isSoloLive = sourceActionConfig.isSoloLive;
    var sourceId = source._id;
    var isLiveSource = (0, _sources.isSourceInProgram)(layers, sourceId);
    var sourceAction = isSoloLive ? _setSoloLive["default"] : _toggleProgramSource["default"];
    var isScreenShare = (0, _sources.isScreenPresenter)(source);
    var sourceValue = (0, _sources.getSourceValue)(source);
    var isAttendeeCameraOn = attendeeCameraStatus[sourceValue] ? true : false;
    var isAttendeeMicOn = attendeeMuteStatus[sourceValue] ? true : false;
    var addingSource = !layers.find(function (layer) {
      return layer.source._id === sourceId;
    });
    var sources = (0, _utils.getBroadcastSourcesFromLayers)(layers, broadcastSources === null || broadcastSources === void 0 ? void 0 : broadcastSources.sources);
    var isCameraAtLimit = addingSource ? (0, _utils.hasMeetingExceededCameraLimit)(attendeeTiles, attendeeCameraStatus) : false;
    var removedSources = isSoloLive ? sources === null || sources === void 0 ? void 0 : sources.filter(function (src) {
      return src._id !== sourceId;
    }) : !addingSource ? [source] : [];
    removedSources === null || removedSources === void 0 || removedSources.forEach(function (src) {
      if ((0, _sources.isCameraPresenter)(src)) {
        LOG.info("ProgramUpdate: Attendee ".concat(src.value, " in meeting ").concat(meetingId, " is removed from the stage"));
      }
    });
    if (!isLiveSource && addingSource && !isCameraAtLimit) {
      LOG.info("ProgramUpdate: Attendee ".concat(source.value, " in meeting ").concat(meetingId, " is added to the stage"));
    }

    // Default state of a presenter being taken live is unmuted
    var newDesiredMuteStatus = false;

    /**
     * This action can potentially dispatch many other actions, so we do them in batch for performance reasons (reduce render passes)
     */
    (0, _reactRedux.batch)(function () {
      /**
       * All of this stuff only applies if autoMutePresenters is turned on
       */
      if (autoMutePresenters) {
        /**
         * If the source is _not_ in program, set desired mute status to false (unmuted)
         */
        if (!isLiveSource) {
          newDesiredMuteStatus = false;
        } else if (!isSoloLive) {
          /**
           * If source is in the program, we are taking them out, so set the desired mute status to true (muted)
           */
          newDesiredMuteStatus = true;
        }

        // if we set a camera presenter solo, we want to mute the sources being removed from program.
        if (isSoloLive && (0, _sources.isCameraPresenter)(source)) {
          //why does this only apply to camera_presenter?? why not audio_presenter??
          layers.filter(function (layer) {
            return (0, _sources.isCameraPresenter)(layer.source) && layer.source._id !== source._id;
          }).forEach(function (layer) {
            var source = layer.source;
            var otherSourceValue = (0, _sources.getSourceValue)(source);
            dispatch(toggleAttendeeMuteStatus(otherSourceValue, true));
          });
        }
      } else {
        newDesiredMuteStatus = isAttendeeMicOn;
      }
      if (isCameraAtLimit) newDesiredMuteStatus = isAttendeeMicOn;

      /**
       * Set the desired attendee camera status to on if it is off.
       *
       *  Do this only if:
       *   - they are _not_ currently in the program
       *   - the source is _not_ a screen share source
       *   - the maximum camera is not reached
       */
      if (!isCameraAtLimit && !isAttendeeCameraOn && !isScreenShare && !isLiveSource && !source.cameraUnavailable) {
        dispatch(toggleAttendeeCameraStatus(sourceValue, true, setProgram));
      }
      dispatch(toggleAttendeeMuteStatus(sourceValue, newDesiredMuteStatus));
      dispatch(sourceAction(source, setProgram));
    });
  };
}
var _default = exports["default"] = takeGenericPresenterLive;